/**
 * Pure logic for the NPM Vista export modal.
 *
 * Everything here exists because the job payload does not say what the UI wants to
 * show. Each function below carries a note on what the backend actually provides,
 * because several of them look like they could be simpler than they are.
 */
import type {
  EmployeeTimeSheetWithDays,
  JobProgressData,
  JobProgressEmployee,
  JobStatus,
  MiscPiecePost,
  PayrollApprovedHourlyProject,
  PayrollApprovedMiscEmployeeTask,
  PiecePostStatus,
  RegularPiecePost,
} from '../../../interfaces/V2Interface';
import { JobStatusEnum } from '../../../interfaces/V2Interface';
import { isServiceOrderRow } from '../../../util/serviceOrderRow';

/* ────────────────────────────────────────────────────────────── selection gate */

/**
 * True if this red is still waiting on a human.
 *
 * THE TEST IS "IS superDescription BLANK", AND ONLY THAT — the same rule the two v1
 * modals use. A blank description means no approver has touched the red; anything
 * written there means one has, whichever workflow they used (V1 approve writes it,
 * the two-level level-2 approver writes it, and a deny copies its reason into it —
 * and a denied red is terminal and zeroed, so there is nothing left to wait on).
 *
 * DO NOT key this on `approvalStatus`. NPM runs two-level approval ON while its
 * approvers still work the V1 misc-task page, which leaves the status at 1 or 2 on a
 * perfectly approved red. The API exports that red; keying on the status made this
 * gate lock the whole employee out of the export instead.
 */
export const isMiscTaskPendingApproval = (misc: PayrollApprovedMiscEmployeeTask): boolean =>
  ((misc.superDescription ?? '') as string).trim() === '';

/** True if any red anywhere on the employee's week is still waiting on a human. */
export const isBlocked = (emp: EmployeeTimeSheetWithDays): boolean =>
  (emp.days ?? []).some((day) =>
    (day.projectEmployeeTasks ?? []).some((pt) =>
      (pt.miscEmployeeTasks ?? []).some(isMiscTaskPendingApproval)));

/** TimeEntryType.Regular — meal punches (2) never export. */
const PUNCH_REGULAR = 1;

/**
 * The exporter's three branches, in the order it tries them.
 *
 * `TimecardProcessor.processTimecard` puts an hourly group into exactly one of job+phase,
 * account code, or service order, and SENDS NOTHING AT ALL for a group that matches none —
 * no row, no error, no trace in the job payload. Everything on this screen that promises
 * what a run will do has to agree with it, so both `volumeOf` and `exportDropGaps` below
 * call this one function rather than each carrying a copy of the test.
 *
 * TAS-517: the service-order arm is `isServiceOrderRow`, the mirror of the API's own
 * module — NOT the old "non-blank ServiceOrderText". Resolving a service order on mobile
 * clears that text, so the old test dropped every resolved punch out of all three branches.
 */
export const postsToVista = (hp: PayrollApprovedHourlyProject): boolean =>
  (!!hp.project?.altProjectID && !!hp.projectPhase?.altProjectPhaseID)
  || !!hp.account?.accountCodeID
  || isServiceOrderRow(hp);

/**
 * What this week will actually send: time-batch entries and piece claims.
 *
 * This mirrors `TimecardProcessor.processTimecard` in the API exactly, so the sub-line
 * on a selection row matches the counts the progress table reports once the job runs.
 * A rough guess here would read as a bug the moment the two disagree.
 *
 * Time-batch entries come from four places, per day:
 *   1. every REGULAR hourly punch that has an out-time AND lands in one of the three
 *      export branches (job+phase, account code, or service order) — a punch matching
 *      none of them silently produces nothing;
 *   2. raw piece punches — one entry each for a kiosk user, or ONE for the whole day
 *      for a piece user (their hours are summed into a single entry);
 *   3. one per sick claim;
 *   4. one per day carrying any meal-penalty exception.
 *
 * Pieces are `employeeTasks` only. Misc tasks ("reds") are a separate third counter in
 * the export and are deliberately not folded in here.
 */
export const volumeOf = (emp: EmployeeTimeSheetWithDays): { time: number; pieces: number } => {
  const kiosk = emp.role?.kioskUser === true;
  let time = 0;
  let pieces = 0;

  for (const day of emp.days ?? []) {
    // 1 — hourly punches
    for (const hp of day.hourlyProjects ?? []) {
      for (const punch of hp.timeEntriesHourly ?? []) {
        if (punch.type !== PUNCH_REGULAR || !punch.outDateTime) continue;
        if (postsToVista(hp)) time += 1;
      }
    }

    // 2 — raw piece punches
    let pieceDayPunches = 0;
    for (const punch of day.timeEntries ?? []) {
      if (punch.type !== PUNCH_REGULAR || !punch.inDateTime || !punch.outDateTime) continue;
      if (kiosk) time += 1; else pieceDayPunches += 1;
    }
    if (!kiosk && pieceDayPunches > 0) time += 1;   // one entry for the whole day

    // 3 — sick claims
    time += (day.employeeSickClaims ?? []).length;

    // 4 — meal penalty, at most one per day
    if (day.fiveHourLunchException || day.fullLunchException || day.noLunchException) time += 1;

    for (const pt of day.projectEmployeeTasks ?? []) {
      pieces += (pt.employeeTasks ?? []).length;
    }
  }

  return { time, pieces };
};

export const formatName = (name: EmployeeTimeSheetWithDays['name']): string =>
  typeof name === 'string' ? name : `${name.first} ${name.middle ? `${name.middle} ` : ''}${name.last}`;

export const initialsOf = (name: EmployeeTimeSheetWithDays['name']): string => {
  const full = formatName(name).trim().split(/\s+/);
  return `${full[0]?.[0] ?? ''}${full.length > 1 ? full[full.length - 1][0] : ''}`.toUpperCase();
};

/* ─────────────────────────────────────── TAS-515/517: hourly punches that won't post */

/** One employee's hourly punches that the exporter will not send. */
export interface ExportDropGap {
  employeeName: string;
  /** Regular hourly punches on a group that none of the exporter's branches take. */
  punches: number;
  /** What those groups carry instead — `Job 24-118 · no phase`, deduped and sorted. */
  reasons: string[];
}

/**
 * Why a group takes none of the exporter's branches, in the operator's words.
 *
 * The realistic case by a distance is a job punch whose phase never got set: the group has
 * an `altProjectID` and no `altProjectPhaseID`, the exporter needs BOTH for its first
 * branch, and there is nothing else on the row to fall back to. Naming the job makes that
 * one fixable in the timesheet instead of merely alarming.
 */
const dropReason = (hp: PayrollApprovedHourlyProject): string => {
  const job = (hp.project?.altProjectID ?? '').trim();
  const phase = (hp.projectPhase?.altProjectPhaseID ?? '').trim();
  if (job && !phase) return `Job ${job} · no phase`;
  if (!job && phase) return `Phase ${phase} · no job`;
  return 'No job, cost code or service order';
};

/**
 * TAS-515/517 — hourly punches this run will silently drop.
 *
 * A group that matches none of the exporter's three branches produces NO Vista row and no
 * complaint: the job still reports every entry it did build as posted. That is TAS-448's
 * failure mode exactly — a clean "173/173 posted" over hours that never arrived — so the
 * only defence is to say the number out loud while the export can still be called off.
 *
 * WHAT THIS IS NO LONGER. Until TAS-517 this listed SERVICE-ORDER punches, because the
 * exporter's third branch keyed on non-blank `ServiceOrderText` and mobile resolving an
 * order clears it, so resolved and temporary punches fell through every branch. The API
 * test is now `isServiceOrderRow` — all three service-order shapes post — so none of them
 * can appear here any more. What is left is the genuinely uncoded punch: no job+phase, no
 * cost code, no service order in any form. Rare, but it was always silently dropped and
 * nothing else on this screen ever mentioned it.
 *
 * `volumeOf` above deliberately does not count these — it mirrors the exporter, and a
 * count that disagreed with what actually posts would read as a bug. This is what that
 * honest count leaves unexplained.
 */
export const exportDropGaps = (
  employees: EmployeeTimeSheetWithDays[]
): ExportDropGap[] => {
  const out: ExportDropGap[] = [];

  for (const emp of employees) {
    let punches = 0;
    const labels = new Set<string>();

    for (const day of emp.days ?? []) {
      for (const hp of day.hourlyProjects ?? []) {
        if (postsToVista(hp)) continue;

        for (const punch of hp.timeEntriesHourly ?? []) {
          // The same two tests volumeOf applies before counting a punch, so the drop
          // total and the planned total are drawn from one population.
          if (punch.type !== PUNCH_REGULAR || !punch.outDateTime) continue;
          punches += 1;
          labels.add(dropReason(hp));
        }
      }
    }

    if (punches > 0) {
      out.push({
        employeeName: formatName(emp.name),
        punches,
        reasons: [...labels].sort(),
      });
    }
  }

  return out.sort((a, b) => a.employeeName.localeCompare(b.employeeName));
};

/** Stable per-name hue for the avatar; same function the board and Sage modal use. */
export const hashHue = (s: string): number => {
  let h = 7;
  for (let i = 0; i < s.length; i++) h = (h * 33 + s.charCodeAt(i)) & 0x7fffffff;
  return h % 360;
};

/* ──────────────────────────────────────────────────────────── piece post status */

export const PIECE_STATUS_LABEL: Record<PiecePostStatus, string> = {
  pending: 'Pending',
  posting: 'Posting',
  succeeded: 'Posted',
  failed: 'Rejected',
  skipped: 'Skipped',
};

/* ─────────────────────────────────────────────────────────────── job outcome */

export type VistaOutcome = 'success' | 'partial' | 'batchFailed' | 'timeout' | 'failed';

/** The API marks a timeout by stamping `errorDetails.timeout`, and only a timeout aborts the job. */
export const isTimeout = (job: JobStatus): boolean => {
  const rd = job.resultData as { errorDetails?: { timeout?: boolean }; error?: string } | null | undefined;
  if (rd?.errorDetails?.timeout === true) return true;
  return typeof rd?.error === 'string' && /timed out/i.test(rd.error);
};

/**
 * Batch creation failing is the trap in this payload: the job returns early, the piece
 * phase never runs, and the job ends **COMPLETED with every count at zero**. The only
 * signal is this flag, so the terminal state has to test it before anything else or it
 * renders "0 of 0 posted" on a total failure.
 */
export const batchCreationFailed = (job: JobStatus): boolean =>
  job.progressData?.timecardBatchCreation === 'failed';

export const outcomeOf = (job: JobStatus, counts: VistaCounts): VistaOutcome => {
  if (batchCreationFailed(job)) return 'batchFailed';
  if (isTimeout(job)) return 'timeout';
  if (job.status === JobStatusEnum.FAILED) return 'failed';
  return counts.failed > 0 ? 'partial' : 'success';
};

/* ────────────────────────────────────────────────────────────────── counts */

export interface VistaCounts {
  posted: number;
  failed: number;
  skipped: number;
  /** posted + failed + skipped — what the run was actually asked to move. */
  total: number;
  timePosted: number;
  timeTotal: number;
  piecesPosted: number;
  piecesTotal: number;
  miscPosted: number;
  miscTotal: number;
  miscSkipped: number;
}

const countPosts = (posts: Array<RegularPiecePost | MiscPiecePost> | undefined, status: PiecePostStatus): number =>
  (posts ?? []).reduce((n, p) => n + (p.status === status ? 1 : 0), 0);

const ZERO: VistaCounts = {
  posted: 0, failed: 0, skipped: 0, total: 0,
  timePosted: 0, timeTotal: 0, piecesPosted: 0, piecesTotal: 0,
  miscPosted: 0, miscTotal: 0, miscSkipped: 0,
};

/**
 * Headline counts for any job state.
 *
 * DERIVED FROM progressData, NOT resultData. `resultData` only carries the count
 * fields on the COMPLETED path — every failure path writes `{error, errorDetails}`
 * instead, so reading counts off it yields NaN exactly when the operator most needs
 * a number. `progressData` is persisted on both timeout exits and retained through
 * the generic catch, so it is the one source that answers on every path.
 *
 * `resultData` is used only as a fallback for a job with no progressData at all.
 */
export const deriveCounts = (job: JobStatus): VistaCounts => {
  const pd = job.progressData;

  if (!pd || !Array.isArray(pd.employees)) {
    const rd = job.resultData as Record<string, number> | null | undefined;
    if (!rd) return ZERO;
    const timePosted = rd.timeEntriesPostedSuccessCount ?? 0;
    const piecesPosted = rd.pieceWorksPostedSuccessCount ?? 0;
    const failed = (rd.timeEntriesPostedFailureCount ?? 0) + (rd.pieceWorksPostedFailureCount ?? 0);
    const skipped = rd.pieceWorksSkippedCount ?? 0;
    const posted = timePosted + piecesPosted;
    return {
      ...ZERO,
      posted, failed, skipped, total: posted + failed + skipped,
      timePosted, timeTotal: rd.totalTimeBatchEntries ?? timePosted,
      piecesPosted, piecesTotal: rd.totalPieces ?? piecesPosted,
      miscTotal: rd.totalMiscPieces ?? 0, miscSkipped: skipped,
    };
  }

  const timePosted = pd.completedTimeBatch ?? 0;
  const timeTotal = pd.totalTimeBatchEntries ?? 0;
  const piecesPosted = pd.completedPieces ?? 0;
  const piecesTotal = pd.totalPieces ?? 0;
  const miscPosted = pd.completedMiscPieces ?? 0;
  const miscTotal = pd.totalMiscPieces ?? 0;

  let piecesFailed = 0;
  let piecesSkipped = 0;
  let miscFailed = 0;
  let miscSkipped = 0;
  for (const emp of pd.employees) {
    piecesFailed += countPosts(emp.regularPiecePosts, 'failed');
    piecesSkipped += countPosts(emp.regularPiecePosts, 'skipped');
    miscFailed += countPosts(emp.miscPiecePosts, 'failed');
    miscSkipped += countPosts(emp.miscPiecePosts, 'skipped');
  }

  // Time posts in chunks and the payload has no per-entry rows, so anything the batch
  // did not carry is treated as failed once the job is terminal.
  const terminal = job.status === JobStatusEnum.COMPLETED || job.status === JobStatusEnum.FAILED;
  const timeFailed = terminal ? Math.max(0, timeTotal - timePosted) : 0;

  const posted = timePosted + piecesPosted + miscPosted;
  const failed = timeFailed + piecesFailed + miscFailed;
  const skipped = piecesSkipped + miscSkipped;

  return {
    posted, failed, skipped, total: posted + failed + skipped,
    timePosted, timeTotal, piecesPosted, piecesTotal,
    miscPosted, miscTotal, miscSkipped,
  };
};

/* ──────────────────────────────────────────────────────── failure classification */

/** The literal the API stamps on every misc piece while posting is switched off. */
export const MISC_DISABLED_MESSAGE = 'Vista misc piece posting disabled';

export interface FailureReason {
  /** Grouping key. Must be stable across occurrences of the same underlying problem. */
  key: string;
  title: string;
  plain: string;
}

const REJECTED_PIECE: FailureReason = {
  key: 'rejectedPiece',
  title: 'Vista rejected these piece claims',
  plain: 'Vista refused the posts. What it objected to is on each row below — open View error for its full response.',
};

const REJECTED_TIME: FailureReason = {
  key: 'rejectedTime',
  title: 'Vista rejected the timecard entries',
  plain: 'Time entries post in batches of up to 1000, so one bad entry rejects the whole batch. The payload does not say which entry or whose, only that the batch did not land.',
};

const ORPHAN: FailureReason = {
  key: 'orphan',
  title: 'Created in Vista, but not submitted',
  plain: 'The misc piece was created in Vista and then the submit step failed, so Vista is holding an order with nothing claimed against it.',
};

const STABLE_PREFIXES: Array<{ match: RegExp; reason: FailureReason }> = [
  {
    match: /^No action ID returned from Vista API/i,
    reason: {
      key: 'noActionId',
      title: 'Vista never acknowledged the request',
      plain: 'Vista accepted the call but returned no action to track, so there is no way to tell whether it did anything with it.',
    },
  },
  {
    match: /^Failed to add data to Vista API/i,
    reason: {
      key: 'addFailed',
      title: 'Vista would not accept the timecard post',
      plain: 'The call to Vista failed outright rather than being rejected for a business reason — usually connectivity or credentials.',
    },
  },
  {
    match: /^Failed to post data to User Defined table/i,
    reason: {
      key: 'udFailed',
      title: 'Vista would not accept the piece post',
      plain: 'The call to the Vista user-defined table failed outright rather than being rejected for a business reason.',
    },
  },
  {
    match: /^Failed to add piece to udJCPieceworks/i,
    reason: {
      key: 'pieceCreateFailed',
      title: 'Vista would not create the misc piece',
      plain: 'The create step failed, so nothing was left behind in Vista for this piece.',
    },
  },
];

/**
 * Pull a human line out of a Vista error payload, if one is in there.
 *
 * WE DO NOT KNOW THE SHAPE. `errorDetails` is `any` on the API side and takes four
 * different forms depending on which call failed; there is not one recorded example
 * of a real Vista rejection anywhere in either repo, and VistaAPIUtil itself carries
 * a "verify shape during live testing" note. So this walks the object looking for the
 * first plausible message-ish string and gives up quietly.
 *
 * It exists so that the first time a real rejection lands in production, the operator
 * probably sees a sentence instead of a blob — and so that when we DO learn the shape,
 * this is the only function that changes.
 */
export const extractVistaMessage = (errorDetails: unknown, errorMessage?: string | null): string | null => {
  const MESSAGE_KEY = /^(message|error|reason|detail|description|errorMessage|statusMessage)$/i;
  const seen = new Set<unknown>();

  const walk = (node: unknown, depth: number): string | null => {
    if (depth > 6 || node == null || typeof node !== 'object' || seen.has(node)) return null;
    seen.add(node);
    if (Array.isArray(node)) {
      for (const item of node) {
        const hit = walk(item, depth + 1);
        if (hit) return hit;
      }
      return null;
    }
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
      if (MESSAGE_KEY.test(k) && typeof v === 'string') {
        const trimmed = v.trim();
        // A whole serialized payload is not a message.
        if (trimmed && trimmed.length <= 300 && !trimmed.startsWith('{') && !trimmed.startsWith('[')) return trimmed;
      }
    }
    for (const v of Object.values(node as Record<string, unknown>)) {
      const hit = walk(v, depth + 1);
      if (hit) return hit;
    }
    return null;
  };

  const fromDetails = walk(errorDetails, 0);
  if (fromDetails) return fromDetails;

  // The envelope message is `Vista API operation failed: {…json…}` — try the JSON tail.
  const tail = errorMessage?.match(/^Vista API operation failed:\s*([[{][\s\S]*)$/);
  if (tail) {
    try {
      return walk(JSON.parse(tail[1]), 0);
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Bucket a failure by something that repeats.
 *
 * The common failure message is `Vista API operation failed: ${JSON.stringify(...)}`,
 * and that payload embeds the action GUID plus created/modified timestamps — so the
 * raw string is unique per occurrence and grouping on it produces N groups for N
 * failures. Everything here therefore groups on the shape of the message, never its
 * text, with the specific reason surfaced per row instead.
 */
export const classifyFailure = (
  errorMessage: string | null | undefined,
  source: 'time' | 'piece' | 'misc',
  failedStep?: MiscPiecePost['failedStep'],
): FailureReason => {
  if (failedStep === 'udJCSubmittedPiecew') return ORPHAN;
  const msg = (errorMessage ?? '').trim();

  for (const { match, reason } of STABLE_PREFIXES) {
    if (match.test(msg)) return reason;
  }
  if (/^Vista API operation failed/i.test(msg) || msg === '') {
    return source === 'time' ? REJECTED_TIME : REJECTED_PIECE;
  }

  // Anything left is already a human sentence — the one Vista path that returns
  // `{target, message}` surfaces here — so it is safe to group on the text itself.
  return {
    key: `msg:${msg.slice(0, 120)}`,
    title: msg.length > 90 ? `${msg.slice(0, 90)}…` : msg,
    plain: 'Vista returned this directly.',
  };
};

/* ─────────────────────────────────────────────────────────────── grouping */

export interface FailureHit {
  employeeName: string;
  altEmployeeID: string;
  /** How many posts of this kind failed for this employee. */
  count: number;
  /** The extracted Vista line, when one could be found. */
  detail: string | null;
  errorMessage: string | null;
  errorDetails: unknown;
  orderNo?: number | null;
  source: 'time' | 'piece' | 'misc';
}

export interface FailureGroup extends FailureReason {
  hits: FailureHit[];
  total: number;
}

const employeeLabel = (emp: JobProgressEmployee): string =>
  `${emp.lastName ?? ''}${emp.firstName ? `, ${emp.firstName}` : ''}`.trim() || (emp.altEmployeeID ?? 'Unknown');

/**
 * Group every failure in the job by reason.
 *
 * Time failures are deliberately collapsed to ONE job-level hit rather than one per
 * employee: the API stamps `timeBatchError` onto every employee in the job, not just
 * the ones in the failed chunk, so per-employee attribution would be fiction.
 */
export const groupFailures = (pd: JobProgressData | undefined): FailureGroup[] => {
  if (!pd?.employees?.length) return [];
  const groups = new Map<string, FailureGroup>();

  const add = (reason: FailureReason, hit: FailureHit) => {
    const existing = groups.get(reason.key);
    if (existing) {
      existing.hits.push(hit);
      existing.total += hit.count;
      return;
    }
    groups.set(reason.key, { ...reason, hits: [hit], total: hit.count });
  };

  // One entry for the whole time batch, if it failed at all.
  const timeError = pd.employees.map((e) => e.timeBatchError).find((m) => !!m) ?? null;
  if (timeError) {
    const timeFailed = Math.max(0, (pd.totalTimeBatchEntries ?? 0) - (pd.completedTimeBatch ?? 0));
    add(classifyFailure(timeError, 'time'), {
      employeeName: 'Timecard batch',
      altEmployeeID: '',
      count: timeFailed || 1,
      detail: extractVistaMessage(undefined, timeError),
      errorMessage: timeError,
      errorDetails: undefined,
      source: 'time',
    });
  }

  for (const emp of pd.employees) {
    const name = employeeLabel(emp);

    const failedPieces = (emp.regularPiecePosts ?? []).filter((p) => p.status === 'failed');
    if (failedPieces.length) {
      const first = failedPieces[0];
      add(classifyFailure(first.errorMessage, 'piece'), {
        employeeName: name,
        altEmployeeID: emp.altEmployeeID ?? '',
        count: failedPieces.length,
        detail: extractVistaMessage(first.errorDetails, first.errorMessage),
        errorMessage: first.errorMessage ?? null,
        errorDetails: first.errorDetails,
        orderNo: first.vistaBody?.OrderNo ?? null,
        source: 'piece',
      });
    }

    const failedMisc = (emp.miscPiecePosts ?? []).filter((p) => p.status === 'failed');
    for (const m of failedMisc) {
      add(classifyFailure(m.errorMessage, 'misc', m.failedStep), {
        employeeName: name,
        altEmployeeID: emp.altEmployeeID ?? '',
        count: 1,
        detail: extractVistaMessage(m.errorDetails, m.errorMessage),
        errorMessage: m.errorMessage ?? null,
        errorDetails: m.errorDetails,
        orderNo: m.vistaOrderNoAfterCreate ?? null,
        source: 'misc',
      });
    }
  }

  return [...groups.values()].sort((a, b) => b.total - a.total);
};

/* ────────────────────────────────────────────────────────── posted-but-check */

export interface SilentWarning {
  employeeName: string;
  text: string;
}

/**
 * Degradations Vista accepted but a human should still look at.
 *
 * ONLY THE COMPANY CASE IS DETECTABLE. The API also silently exports earn code 0 when
 * an account code is missing, and silently swallows a per-employee crash — neither
 * leaves any trace in this payload (EarnCode never enters progressData at all, and a
 * swallowed crash is indistinguishable from an employee with an empty timesheet or
 * from a time-only export). Do not add rows for those without a backend change.
 */
export const silentWarnings = (pd: JobProgressData | undefined): SilentWarning[] => {
  if (!pd?.employees?.length) return [];
  const out: SilentWarning[] = [];
  for (const emp of pd.employees) {
    const bad = (emp.regularPiecePosts ?? []).filter(
      (p) => p.status === 'succeeded' && (p.vistaBody?.JCCo == null || p.vistaBody?.JCCo === 0));
    if (bad.length) {
      out.push({
        employeeName: employeeLabel(emp),
        text: `posted ${bad.length} piece claim${bad.length === 1 ? '' : 's'} with no company on them — check they landed in the right Vista company.`,
      });
    }
  }
  return out;
};

/* ─────────────────────────────────────────────────────────── batch identifiers */

export interface BatchIds {
  batchId: number | string | null;
  keyID: number | string | null;
}

/**
 * `batchId`/`keyID` are written to `resultData` only on the COMPLETED path. A job that
 * timed out mid-post DID create a batch in Vista, but the payload does not carry its
 * number — hence the "unavailable" copy on the timeout screen rather than an ID.
 */
export const batchIdsOf = (job: JobStatus): BatchIds => {
  const rd = job.resultData as { batchId?: number | string; keyID?: number | string } | null | undefined;
  return { batchId: rd?.batchId ?? null, keyID: rd?.keyID ?? null };
};

/** True when a batch was created in Vista but this run cannot say which one. */
export const batchExistsButUnknown = (job: JobStatus): boolean => {
  const { batchId } = batchIdsOf(job);
  return batchId == null && !batchCreationFailed(job) && (job.progressData?.completedTimeBatch ?? 0) > 0;
};
